import type { ColDef, RowClickedEvent, RowDragEndEvent } from 'ag-grid-community';
import { type FC, useContext, useEffect, useState } from 'react';
import { Button, OverlayTrigger, Popover } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import EditblackIcon from '../../../assets/edit-icon.svg';
import VerticalDotMenuIcon from '../../../assets/vertical-dot-menu.svg';
import { unknownErrorMessage } from '../../../constants/generalData';
import { UserPropertiesContext } from '../../../modules/context/userPropertiesContext';
import { PackageStatus } from '../../../modules/enums/status';
import { useAuth, useNotifications, useRequest } from '../../../modules/hooks';
import type { PackagesResponse } from '../../../modules/interfaces';
import { endpoints } from '../../../modules/mappers/urls';
import { DataTable } from '../../Tables/DataTable';
import { EditPackagePriorityModal, type PriorityValue } from './EditPackagePriority';

interface PropertyConfigModalProps {
  show: boolean;
  packagesData: PackagesResponse[];
  handleClose: () => void;
  onSavePriority: () => void;
}

export const PackagePriorityModal: FC<PropertyConfigModalProps> = ({
  show,
  packagesData,
  handleClose,
  onSavePriority,
}) => {
  const { credentialsInfo } = useAuth()!;
  const [packages, setPackages] = useState<PackagesResponse[]>([]);
  const { setSimpleToasts } = useNotifications()!;
  const [editPackageValue, setEditPackageValue] = useState<PackagesResponse>();
  const { selectedProperty } = useContext(UserPropertiesContext)!;

  const [showEditPriority, setShowEditPriority] = useState(false);

  const [
    { loading: loadingPriorities, data: priorityData, error: prioritiesError },
    updatePriority,
  ] = useRequest(
    endpoints.PACKAGES_PRIORITIES,
    'put',
    {
      authToken: credentialsInfo?.token,

      propertyId: selectedProperty?.property.uuid || undefined,
    },
    { manual: true },
  );

  const onClose = () => {
    setPackages([]);
    handleClose();
  };

  useEffect(() => {
    if (packagesData && show) {
      const enabledPackages = packagesData.filter(
        (packageValue) => packageValue.status === PackageStatus.ACTIVE,
      );
      setPackages(enabledPackages);
    }
  }, [packagesData, show]);

  useEffect(() => {
    if (priorityData) {
      onSavePriority();
      onClose();
    }

    if (prioritiesError) {
      setSimpleToasts({
        message: prioritiesError.response?.data.error.message || unknownErrorMessage,
        type: 'danger',
        show: true,
      });
    }
  }, [priorityData]);

  const onShowEditModal = (item?: PackagesResponse) => {
    setEditPackageValue(item);
    setShowEditPriority(true);
  };

  const renderMenuIcon = (item: RowClickedEvent<PackagesResponse>) => {
    return (
      <div className="menu-icon-container-style">
        <OverlayTrigger
          trigger="click"
          placement="left"
          rootClose
          overlay={
            <Popover id="popover-basic">
              <Popover.Body>
                <div
                  onClick={(e) => {
                    document.body.click();

                    onShowEditModal(item.data);
                  }}
                  style={{ paddingBottom: '5px' }}
                  className="edit-damage-report-container"
                >
                  <img
                    className="edit-icon-image-style"
                    src={EditblackIcon}
                    alt="edit-black-icon"
                  />
                  <div className="edit-text-style">Edit priority</div>
                </div>
              </Popover.Body>
            </Popover>
          }
        >
          <div>
            <img src={VerticalDotMenuIcon} alt="vertical-icon" />
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const [columns] = useState<ColDef<PackagesResponse>[]>([
    { field: 'priority', rowDrag: true },
    { field: 'name', headerName: 'Add-on name', flex: 2 },
    { field: 'description', headerName: 'Description', flex: 4 },
    {
      headerName: 'Actions',
      cellRenderer: renderMenuIcon,
      flex: 0.3,
    },
  ]);

  const onSave = () => {
    const priorities = packages.map((packageValue) => ({
      packageId: packageValue?.uuid,
      priority: packageValue?.priority,
    }));

    updatePriority({ data: priorities });
  };

  const onRowDragEnd = (event: RowDragEndEvent) => {
    const updated = event.api.getDisplayedRowCount()
      ? Array.from({ length: event.api.getDisplayedRowCount() }, (_, index) => {
          const node = event.api.getDisplayedRowAtIndex(index)!;
          return {
            ...node.data,
            priority: index + 1,
          };
        })
      : [];

    setPackages(updated);
  };

  function updatePackagesByPriority(priorityData: PriorityValue) {
    if (!priorityData?.priority || !priorityData?.uuid) return;

    const movingItemIndex = packages.findIndex(
      (packageValue) => packageValue.uuid === priorityData.uuid,
    );

    if (movingItemIndex === -1) return;

    const [movingItem] = packages.splice(movingItemIndex, 1);

    const newPosition = Math.max(0, Math.min(priorityData.priority - 1, packages.length));

    packages.splice(newPosition, 0, movingItem);

    const updated = packages.map((packageValue, index) => ({
      ...packageValue,
      priority: index + 1,
    }));

    setPackages(updated);
  }

  return (
    <Modal
      show={show}
      onHide={() => {
        handleClose();
      }}
      size="xl"
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title className="h5">Add-on Priorities</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mt-16px">
          <DataTable
            showSearchInput={false}
            changeState={0}
            rowData={packages}
            columnDefs={columns}
            pagination
            component="properties"
            paginationPageSize={20}
            rowDragManaged
            onRowDragEnd={onRowDragEnd}
            rowDragEntireRow
          />
        </div>
      </Modal.Body>
      <Modal.Footer style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <div>
          <Button
            className="cancel-button"
            style={{ marginRight: '10px' }}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="save-button"
            type="submit"
            onClick={onSave}
            disabled={loadingPriorities}
          >
            {loadingPriorities ? 'Loading...' : 'Save'}
          </Button>
        </div>
      </Modal.Footer>

      {editPackageValue && (
        <EditPackagePriorityModal
          show={showEditPriority}
          packagesData={editPackageValue}
          handleClose={() => setShowEditPriority(false)}
          onSave={updatePackagesByPriority}
          maxPriority={packagesData.length}
        />
      )}
    </Modal>
  );
};
